// Carry Roll ConditionsInputs — collapsible environment inputs (5.1-5.4 + advanced 5.5-5.11).

import { Select } from 'calc-controls';
import { Collapsible } from './Collapsible';
import { LabeledNumber } from './LaunchInputs';
import { HelpTooltip } from './HelpTooltip';
import { useStore } from '../../store';
import { useInputMode } from '../../store/selectors';
import { Firmness, InputMode, LandingSurface, SurfaceMoisture, type Firmness as FirmnessT } from '@carry-roll/shared-types';
import { mToFt, ftToM, msToMph, mphToMs, msToKph, kphToMs } from '@carry-roll/shared-logic';

export function ConditionsInputs() {
  const env = useStore((s) => s.request.environment);
  const setEnvironment = useStore((s) => s.setEnvironment);
  const heightUnit = useStore((s) => s.heightUnit);
  const speedUnit = useStore((s) => s.speedUnit);
  const advanced = useInputMode() === InputMode.Advanced;

  return (
    <Collapsible title="Conditions" testId="conditions-inputs" helpLabel="Environmental conditions">
      <LabeledNumber
        label={`Altitude (${heightUnit})`}
        ariaLabel="Altitude"
        value={heightUnit === 'ft' ? mToFt(env.altitudeM) : env.altitudeM}
        onChange={(v) => setEnvironment({ altitudeM: heightUnit === 'ft' ? ftToM(v) : v })}
        step={10}
        help="Higher altitude → thinner air → less drag → more carry."
      />
      <LabeledNumber
        label={`Temperature (°C)`}
        ariaLabel="Temperature in Celsius"
        value={Math.round((env.temperatureK - 273.15) * 10) / 10}
        onChange={(v) => setEnvironment({ temperatureK: v + 273.15 })}
        step={0.5}
        help="Ambient air temperature. Warmer air is less dense, adding a little carry."
      />
      <LabeledNumber
        label={`Wind speed (${speedUnit})`}
        ariaLabel="Wind speed"
        value={speedUnit === 'mph' ? msToMph(env.windSpeed) : msToKph(env.windSpeed)}
        onChange={(v) => setEnvironment({ windSpeed: speedUnit === 'mph' ? mphToMs(v) : kphToMs(v) })}
        step={0.5}
        help="Headwind (0°) costs distance; tailwind (180°) adds it; crosswind shifts finish."
      />
      <LabeledNumber
        label="Wind direction (deg, 0=head)"
        ariaLabel="Wind direction"
        value={env.windDirDeg}
        onChange={(v) => setEnvironment({ windDirDeg: v })}
        step={1}
        help="Wind bearing in degrees. 0 = headwind into the shot; 180 = tailwind; 90/270 = crosswind."
      />
      <div className="block">
        <span className="field-label">
          Ground firmness
          <HelpTooltip label="Ground firmness">Ground firmness. Firm = more roll; soft = less roll.</HelpTooltip>
        </span>
        <Select
          inline
          label="Ground firmness"
          value={env.firmness}
          onChange={(v) => setEnvironment({ firmness: v as FirmnessT })}
          options={[
            { value: Firmness.Firm, label: 'Firm' },
            { value: Firmness.Normal, label: 'Normal' },
            { value: Firmness.Soft, label: 'Soft' },
          ]}
        />
      </div>
      {advanced && (
        <>
          <div className="block">
            <span className="field-label">
              Moisture
              <HelpTooltip label="Surface moisture">Turf moisture. Wet ground increases friction and shortens roll.</HelpTooltip>
            </span>
            <Select
              inline
              label="Surface moisture"
              value={env.moisture}
              onChange={(v) => setEnvironment({ moisture: v as SurfaceMoisture })}
              options={[
                { value: SurfaceMoisture.Dry, label: 'Dry' },
                { value: SurfaceMoisture.Normal, label: 'Normal' },
                { value: SurfaceMoisture.Wet, label: 'Wet' },
              ]}
            />
          </div>
          <div className="block">
            <span className="field-label">
              Landing surface
              <HelpTooltip label="Landing surface">Where the ball first lands. Green and rough grip more than fairway.</HelpTooltip>
            </span>
            <Select
              inline
              label="Landing surface"
              value={env.landingSurface}
              onChange={(v) => setEnvironment({ landingSurface: v as LandingSurface })}
              options={[
                { value: LandingSurface.Fairway, label: 'Fairway' },
                { value: LandingSurface.Rough, label: 'Rough' },
                { value: LandingSurface.Green, label: 'Green' },
              ]}
            />
          </div>
          <LabeledNumber
            label="Landing slope (deg)"
            ariaLabel="Landing slope"
            value={env.landingSlopeDeg}
            onChange={(v) => setEnvironment({ landingSlopeDeg: v })}
            step={0.5}
            help="Slope of the landing zone along the shot line. Downhill (negative) extends roll; uphill shortens it."
          />
          <LabeledNumber
            label="Cross-slope (deg)"
            ariaLabel="Cross slope"
            value={env.crossSlopeDeg}
            onChange={(v) => setEnvironment({ crossSlopeDeg: v })}
            step={0.5}
            help="Lateral ground slope during rollout. Positive drifts the finish right; negative left."
          />
          <LabeledNumber
            label={`Elevation differential (${heightUnit})`}
            ariaLabel="Elevation differential"
            value={heightUnit === 'ft' ? mToFt(env.elevationDiffM) : env.elevationDiffM}
            onChange={(v) => setEnvironment({ elevationDiffM: heightUnit === 'ft' ? ftToM(v) : v })}
            step={1}
            help="Height difference from tee to target. A downhill (negative) target lengthens carry."
          />
          <label className="block">
            <span className="field-label">
              Sea-level baseline
              <HelpTooltip label="Sea-level baseline">Forces the air-density model to standard sea-level temperature and pressure, ignoring your entries.</HelpTooltip>
            </span>
            <input
              type="checkbox"
              checked={env.seaLevelBaseline}
              onChange={(e) => setEnvironment({ seaLevelBaseline: e.target.checked })}
            />
          </label>
          <LabeledNumber
            label="Humidity (fraction)"
            ariaLabel="Relative humidity"
            value={env.humidity}
            onChange={(v) => setEnvironment({ humidity: v })}
            step={0.05}
            help="Relative humidity as a fraction 0–1. Higher humidity slightly lowers air density."
          />
          <LabeledNumber
            label="Pressure (Pa)"
            ariaLabel="Barometric pressure"
            value={env.pressurePa}
            onChange={(v) => setEnvironment({ pressurePa: v })}
            step={100}
            help="Barometric pressure in pascals. Lower pressure (e.g. a storm front) reduces drag."
          />
        </>
      )}
    </Collapsible>
  );
}
